import update from "immutability-helper";
import { findIndex as _findIndex } from "lodash-es";
import slugify from "slugify";

export const slugifyToLowercase = (string) => slugify(string, { lower: true });
export const getHumanUrl = (human) =>
  `/h/${human.id}/${slugifyToLowercase(human.name)}`;

export const focusOnRender = (node) => {
  if (node !== null) {
    node.focus();
  }
};

export const escapeRegExp = (string) =>
  string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// eslint-disable-next-line no-useless-escape
const ESCAPE_NAME_SUMBOLS = /[\@|\[|\]|\(|\)]/g;

export const escapeNameForHumanTag = (name) => {
  return name.replace(/\s+/g, " ").replace(ESCAPE_NAME_SUMBOLS, "-");
};

export const makeHumanTagChecker = (humanId) => {
  const testRe = new RegExp(
    `@\\[([^\\@|\\[|\\]|\\(|\\)]*?)\\]\\(${humanId}\\)`,
    "",
  );
  return (comment) => testRe.test(comment);
};

export const makeHumanTagNameSlugUpdater = (humanId, newName) => {
  const findRe = new RegExp(
    `@\\[([^\\@|\\[|\\]|\\(|\\)]*?)\\]\\(${humanId}\\)`,
    "g",
  );
  const newTag = `@[${escapeNameForHumanTag(newName)}](${humanId})`;

  return (comment) => comment.replace(findRe, newTag);
};

// eslint-disable-next-line no-useless-escape
const RE_HUMAN_TAG = /@\[([^\@|\[|\]|\(|\)]*?)\]\(([0-9a-f]+)\)/g;

export const replaceHumanTagsWithSlugs = (comment) =>
  comment.replace(RE_HUMAN_TAG, "$1");

export const genUniqueId = () => new Date().valueOf().toString(16);

// After login/register, mobile should land on the humans list (not the new-note view).
// Keep in sync with the routing expectations in `pages/App.js`.
export const getPostAuthRedirectPath = () => {
  // Default to desktop behavior in non-browser contexts.
  if (typeof window === "undefined") return "/h/";

  try {
    return window.matchMedia("(max-width: 1025px)").matches ? "/h/list" : "/h/";
  } catch {
    return "/h/";
  }
};

export const checkTouchDevice = () =>
  "ontouchstart" in window ||
  (window.DocumentTouch && document instanceof window.DocumentTouch) ||
  navigator.maxTouchPoints > 0 ||
  window.navigator.msMaxTouchPoints > 0;

export const getDateISOString = (date) => date.toISOString().substring(0, 10); // YYYY-MM-DD

export const autoresizeTextarea = (textareaRef) => {
  const el = textareaRef.current;
  if (!el) return;

  el.style.resize = "none";

  // lock parent height, to not trigger scroll
  // because of textarea shrinking temporarily
  const elParent = el.parentElement;
  elParent.style.height = `${el.offsetHeight + 2}px`;

  // autoresize
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight + 2}px`;

  // update parent height after resize
  elParent.style.height = `${el.offsetHeight + 2}px`;
};

const PENDING_FLAGS_SPEC = {
  $merge: {
    pending: true,
    modified: false,
  },
};
const REVERT_PENDING_FLAGS_SPEC = {
  $merge: {
    pending: false,
    modified: true,
  },
};
export const setPendingFlag = (state, data, pending) => {
  const u = pending ? PENDING_FLAGS_SPEC : REVERT_PENDING_FLAGS_SPEC;
  const spec = {};

  for (const id in data) {
    spec[_findIndex(state, { id })] = u;
  }

  return update(state, spec);
};

export const makeObjectsSaved = (state, data) => {
  const spec = {};

  for (let id in data) {
    const updatedFields = data[id];

    const index = _findIndex(state, { id });
    const obj = state[index];

    if (obj.modified) {
      // if was modified during request
      spec[index] = {
        $merge: {
          syncedAt: updatedFields.syncedAt,
          pending: false,
        },
      };
    } else if (updatedFields.error === "conflict") {
      // TODO: handle conflicts properly
      spec[index] = {
        $merge: {
          ...updatedFields.object, // overwrites local changes
          pending: false,
        },
      };
    } else if (updatedFields.error) {
      spec[index] = {
        $merge: {
          modified: true,
          pending: false,
        },
      };
    } else {
      spec[index] = {
        $merge: {
          ...updatedFields,
          pending: false,
        },
      };
    }
  }
  return update(state, spec);
};

export const updateFromFetchUpdates = (state, updates) => {
  // handles FETCH_UPDATES_SUCCESS

  // TODO: check id not in deleted objects store

  const spec = {
    $push: [],
    $splice: [], // To remove: [index, 1]
  };

  for (const newObj of updates) {
    const objIndex = _findIndex(state, { id: newObj.id });

    if (objIndex < 0) {
      if (!newObj.deleted) {
        spec.$push.push(newObj);
      }
      continue;
    }

    if (newObj.deleted) {
      spec.$splice.push([objIndex, 1]);
      continue;
    }

    const obj = state[objIndex];

    if (!obj.modified && !obj.pending) {
      spec[objIndex] = { $set: newObj };
    }
  }

  return update(state, spec);
};

export function extractEmails(text) {
  return text.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi);
}

// for long suffixes use dayjs instead
export function timeAgo(date) {
  let seconds = Math.floor((new Date() - date) / 1000);

  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + "y";

  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + "mo";

  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + "d";

  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + "h";

  interval = seconds / 60;
  return Math.floor(interval) + "m";
}
